import PropTypes from "prop-types"
import React, { useEffect, useRef, useState } from "react"
import { connect } from "react-redux"
import { useLocation, withRouter } from "react-router-dom"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Modal,
  ModalBody,
  ModalHeader,
  Row
} from "reactstrap"
import { AvForm } from "availity-reactstrap-validation"
import { withTranslation } from "react-i18next"
import toastr from "toastr"

import Breadcrumbs from "components/Common/Breadcrumb"
import AlertaSemVinculo from "../../components/Common/alerta-sem-vinculo"
import FormFilterCompany from "../../components/Common/form-filter-company"
import usePermission from "helpers/permission/usePermission"
import { getCompanies, getCompaniesValuesByOptions } from "../../helpers/utils"
import { Filters } from "../../common/data/definitions"
import { navegarBreadCrumb } from "../../store/navigator/actions"
import {
  apiBuscarNotificacoes,
  apiBuscarNotificacoesUsuario,
  apiRemoverDispositivo
} from "../../helpers/backend_helper"

import DispositivosTable from "./dispositivos-table"
import DispositivoEditarModal from "./dispositivo-editar-modal"
import DispositivoLogsNotificacoesModal from "./dispositivo-modal"

const CardapioDispositivosLista = props => {
  const { t } = props
  const ref = useRef()
  const location = useLocation()

  const isInforangra = props.user?.usuario_info !== null
  const possuiVinculo = (props.user?.vinculos || []).some(v => ["A", "T"].includes(v.vinculo_status))

  const usePermissionEdit = usePermission("dispositivos_editar")
  const usePermissionDelete = usePermission("dispositivos_excluir")

  const [companiesOption, setCompaniesOption] = useState([])
  const [companies, setCompanies] = useState([])
  const [filters, setFilters] = useState(null)

  const [modalLogs, setModalLogs] = useState(false)
  const [modalEditar, setModalEditar] = useState(false)
  const [modalRemove, setModalRemove] = useState(false)
  const [dispositivoSelecionado, setDispositivoSelecionado] = useState(null)
  const [dispositivoParaExcluir, setDispositivoParaExcluir] = useState(null)

  useEffect(() => {
    setCompaniesOption(getCompanies(props.user))
    props.navegarBreadCrumb({ title: "breadcrumb.menu.devices", link: location.pathname })
    populateFiltros([])
  }, [])

  const populateFiltros = (companiesSelected) => {
    const f = []
    const selecionadas = Array.isArray(companiesSelected) ? companiesSelected : companies
    if (selecionadas && selecionadas.length > 0) {
      f.push({ id: "idEmpresa", value: getCompaniesValuesByOptions(selecionadas) })
    }
    if (isInforangra) f.push({ id: "ignoreCompany", value: true, condition: Filters.equals })
    setFilters(f)
    return f
  }

  const pesquisar = () => {
    populateFiltros(companies)
    setTimeout(() => ref?.current?.search(), 300)
  }

  const onVisualizarLogs = (dispositivo) => {
    setDispositivoSelecionado(dispositivo)
    setModalLogs(true)
  }

  const onEditar = (dispositivo) => {
    setDispositivoSelecionado(dispositivo)
    setModalEditar(true)
  }

  const onExcluir = (dispositivo) => {
    setDispositivoParaExcluir(dispositivo)
    setModalRemove(true)
  }

  const remover = async () => {
    if (!dispositivoParaExcluir) return
    try {
      await apiRemoverDispositivo({ id: dispositivoParaExcluir.id })
      toastr.success(t("cardapio.dispositivo.excluir.sucesso.titulo"))
      setModalRemove(false)
      setDispositivoParaExcluir(null)
      pesquisar()
    } catch (e) {
      toastr.error(
        e?.response?.data?.message || "",
        t("cardapio.dispositivo.excluir.erro.titulo")
      )
    }
  }

  if (!props.user || !possuiVinculo) return <AlertaSemVinculo />

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="breadcrumb.menu.devices" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <Form>
                    <Row>
                      <div className="col col-12 col-md-4 col-lg-3">
                        <FormGroup className="mt-3 mb-0">
                          <FormFilterCompany
                            attribute="companies"
                            value={ companies }
                            description={ t("cardapio.dispositivo.lista.filtro.empresa.label") }
                            setValue={ setCompanies }
                            options={ companiesOption }
                            multiple={ true }
                            hasSelectAll={ !isInforangra }
                          />
                        </FormGroup>
                      </div>
                      <div className="col col-auto align-self-start mt-md-4 pt-1">
                        <div className="mt-3 col col-auto">
                          <Button color="primary" className="w-md" onClick={ pesquisar }>
                            { t("cardapio.dispositivo.lista.filtro.botao.buscar") }
                          </Button>
                        </div>
                      </div>
                    </Row>
                  </Form>
                </CardBody>
              </Card>

              { filters && (
                <Card>
                  <CardBody>
                    <DispositivosTable
                      ref={ ref }
                      filters={ filters }
                      user={ props.user }
                      usePermissionEdit={ usePermissionEdit }
                      usePermissionDelete={ usePermissionDelete }
                      onVisualizarLogs={ onVisualizarLogs }
                      onEditar={ onEditar }
                      onExcluir={ onExcluir }
                    />
                  </CardBody>
                </Card>
              ) }
            </Col>
          </Row>
        </Container>
      </div>

      <DispositivoEditarModal
        isOpen={ modalEditar }
        dispositivo={ dispositivoSelecionado }
        onClose={ () => {
          pesquisar();
          setModalEditar(false);
        }}
        onSaved={ () => pesquisar() }
      />
      <DispositivoLogsNotificacoesModal
        isOpen={ modalLogs }
        dispositivo={ dispositivoSelecionado }
        onClose={ () => setModalLogs(false) }
      />

      <Modal isOpen={ modalRemove }>
        <ModalHeader tag="h4">
          Confirmação
        </ModalHeader>
        <ModalBody>
          <AvForm onValidSubmit={ (e, v) => remover() }>
            <Row form>
              <Col className="col-12">
                { t("cardapio.dispositivo.excluir.confirmacao.titulo") }
              </Col>
            </Row>
            <Row style={ { paddingTop: 10 } }>
              <Col>
                <div className="text-right">
                  <button type="submit" className="btn btn-success save-event">
                    { t("cardapio.dispositivo.excluir.confirmacao.botao.confirmar") }
                  </button>
                  <button
                    type="button"
                    className="btn btn-light ms-2"
                    onClick={ e => setModalRemove(false) }
                  >
                    { t("cardapio.dispositivo.excluir.confirmacao.botao.cancelar") }
                  </button>
                </div>
              </Col>
            </Row>
          </AvForm>
        </ModalBody>
      </Modal>
    </React.Fragment>
  )
}

CardapioDispositivosLista.propTypes = {
  t: PropTypes.func,
  user: PropTypes.any,
  navegarBreadCrumb: PropTypes.func
}

const mapStateToProps = state => {
  const { user } = state.Login
  return { user }
}

export default withTranslation()(connect(mapStateToProps, { navegarBreadCrumb })(
  withRouter(CardapioDispositivosLista)
))
